/**
 * @fileoverview DateRenderer component for the Storyteller web application.
 * Displays a date input question.
 *
 * @example
 * // Basic usage
 * <DateRenderer schema={schema} uischema={uischema} />
 */

import { ControlProps, rankWith, uiTypeIs } from '@jsonforms/core'
import { withJsonFormsControlProps } from '@jsonforms/react'
import { VanillaRendererProps, withVanillaControlProps } from '@jsonforms/vanilla-renderers'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { NativeDropdown } from '../../../base/NativeDropdown'
import css from './DateRenderer.module.css'

/**
 * @function DateRenderer
 * @description Component for rendering a date input question.
 * @param {VanillaRendererProps & ControlProps} props - The props for the DateRenderer component.
 * @returns {JSX.Element} The DateRenderer component.
 */
function DateRenderer(props: VanillaRendererProps & ControlProps) {
    const { path, errors, handleChange } = props
    const [data, setData] = useState<Record<'day' | 'month' | 'year', string>>({
        day: undefined,
        month: undefined,
        year: undefined,
    })
    const { t } = useTranslation()
    const hasError = errors.length > 0

    useEffect(() => {
        handleChange(path, data)
    }, [path, data, handleChange])

    function onChange(key: string, value: unknown) {
        setData({ ...data, [key]: value })
    }

    return (
        <div>
            <div className={css.container}>
                <NativeDropdown
                    onChange={(value) => onChange('month', value)}
                    errors={errors}
                    value={data.month}
                    placeholder={t('placeholders.month')}
                    className={css.month}
                    options={new Array(12)
                        .fill(0)
                        .map((_, i) => i + 1)
                        .map((month) => ({
                            const: month.toString(),
                            title: t(`months.${month}`),
                        }))}
                    path="month"
                    hideErrorMessage
                    required
                />
                <div className={css.group}>
                    <NativeDropdown
                        onChange={(value) => onChange('day', value)}
                        errors={errors}
                        value={data.day}
                        placeholder={t('placeholders.day')}
                        className={css.day}
                        options={new Array(31)
                            .fill(0)
                            .map((_, i) => i + 1)
                            .map((day) => ({
                                const: day.toString(),
                                title: day.toString(),
                            }))}
                        path="day"
                        hideErrorMessage
                        required
                    />
                    <NativeDropdown
                        onChange={(value) => onChange('year', value)}
                        errors={errors}
                        value={data.year}
                        placeholder={t('placeholders.year')}
                        className={css.year}
                        options={new Array(130)
                            .fill(0)
                            .map((_, i) => i + new Date(Date.now()).getFullYear() - 129)
                            .map((year) => ({
                                const: year.toString(),
                                title: year.toString(),
                            }))}
                        path="year"
                        hideErrorMessage
                        required
                    />
                </div>
            </div>
            {hasError && <div className={css.errorMessage}>{errors}</div>}
        </div>
    )
}

export const DateTester = rankWith(3, uiTypeIs('Date'))

export default withVanillaControlProps(withJsonFormsControlProps(DateRenderer))
